import { useEffect, useRef, useState } from "react";

import ChevronLeftIcon from "@heroicons/react/20/solid/ChevronLeftIcon";
import ChevronRightIcon from "@heroicons/react/20/solid/ChevronRightIcon";
import { SliceZone } from "@prismicio/react";
import _isEqual from "lodash/isEqual";
import qs from "qs";
import {
  useForm,
  FormProvider,
  FieldValues,
  SubmitHandler,
} from "react-hook-form";

import PrimaryButton from "components/button/primaryButton/PrimaryButton";
import TertiaryButton from "components/button/tertiaryButton/TertiaryButton";
import ErrorBoundary from "components/errorBoundary/ErrorBoundary";
import TabbedForm from "components/form/tabbedForm/TabbedForm";
import { FormDocument, PreviewDocument } from "prismicio-types";
import { useGetProduct, useSetCustomisation } from "queries/product-queries";
import { components as fieldComponents } from "slices/fields";
import { components as previewComponents } from "slices/previews";
import { SSR_CONFIGURATION_ERROR, ErrorItem } from "utils/error-utils";
import { CANVAS_FALLBACK_CONTENT } from "utils/fallback-content";
import { useGetDefaultValuesFromFormDocuments } from "utils/form-utils";
import { useInterpolate } from "utils/prismic-utils";
import {
  usePartialTocValidation,
  useGetTabsFromFormDocument,
  getPrismicKeyFromErrorType,
} from "utils/schema-utils";

import Modal, { ModalProps } from "../Modal";

import styles from "./SchemaFormModal.module.scss";

type SchemaFormModalProps = Omit<ModalProps, "children"> & {
  currentEditIndex?: number;
  formDocument: FormDocument;
  numberOfEdits?: number;
  onStepEdit?: (direction: "next" | "previous") => void;
};

/**
 * Modal for forms powered by Muse schemas.
 *
 * Loops over the fields in the provided schema and outputs a SchemaField for each.
 *
 * Triggered by clicking drawers and "Edit page" buttons.
 */
export default function SchemaFormModal({
  currentEditIndex,
  formDocument,
  numberOfEdits,
  onDismiss,
  onStepEdit,
  ...props
}: SchemaFormModalProps) {
  const defaultValues = useGetDefaultValuesFromFormDocuments([formDocument]);
  const prevFormDocument = useRef(formDocument);

  const methods = useForm({
    defaultValues,
  });

  const { data: product } = useGetProduct();
  const { mutate: setCustomisation } = useSetCustomisation();

  if (!product) {
    throw SSR_CONFIGURATION_ERROR;
  }

  const validate = usePartialTocValidation();
  const interpolate = useInterpolate();

  const [status, setStatus] = useState<"idle" | "loading" | "error">("idle");
  const [activeTabIndex, setActiveTabIndex] = useState(0);

  const preview =
    (formDocument.data.preview as unknown as PreviewDocument).data ?? null;
  const tabs = useGetTabsFromFormDocument(formDocument);

  /**
   * Reset form when form document changes
   *
   * Happens when a user steps to the next or previous edit
   */
  useEffect(() => {
    if (!_isEqual(formDocument, prevFormDocument.current)) {
      // Don't override fields the user has altered
      methods.reset(defaultValues);
      prevFormDocument.current = formDocument;
    }
  }, [defaultValues, methods, formDocument]);

  // Set validation errors in RHF, using messages from the Canvas Prismic document
  function setErrors(errors: ErrorItem[]) {
    errors.forEach((error) => {
      methods.setError(error.context.label, {
        type: error.type,
        message: product!.content.canvas[
          getPrismicKeyFromErrorType(error.type)
        ] as string,
      });
    });
  }

  async function handleDismiss(data?: qs.ParsedQs) {
    if (data) {
      setCustomisation({ data });
    }

    onDismiss();
  }

  const validateSubmission = async (data: FieldValues) => {
    // Run local validation first
    if (!(await methods.trigger(undefined, { shouldFocus: true }))) {
      return false;
    }

    setStatus("loading");

    try {
      const { combinedCustomisation, errors } = await validate(data);

      setStatus("idle");

      if (errors) {
        setErrors(errors);
        return false;
      }

      return combinedCustomisation;
    } catch (error) {
      /**
       * Error isn't validation-related.
       *
       * We don't want to close the form because this could be
       * a client-side (e.g. network) or temporary API issue (e.g. timeout)
       * that a retry would fix.
       */
      setStatus("error");
      return false;
    }
  };

  const onSubmit: SubmitHandler<FieldValues> = async (data) => {
    const combinedCustomisation = await validateSubmission(data);

    if (combinedCustomisation) {
      if (tabs && activeTabIndex < tabs.length - 1) {
        setCustomisation({ data: combinedCustomisation });
        setActiveTabIndex((t) => t + 1);
      } else {
        handleDismiss(data);
      }
    }
  };

  async function onTabChange(index: number) {
    if (index === activeTabIndex) {
      return;
    }

    methods.handleSubmit(async (data) => {
      const combinedCustomisation = await validateSubmission(data);

      if (combinedCustomisation) {
        setCustomisation({ data: combinedCustomisation });
        setActiveTabIndex(index);
      }
    })();
  }

  async function onSubmitAndStep(data: any, direction: "next" | "previous") {
    const combinedCustomisation = await validateSubmission(data);

    if (combinedCustomisation) {
      setCustomisation({ data: combinedCustomisation });
      onStepEdit?.(direction);
    }
  }

  return (
    <Modal onDismiss={() => handleDismiss()} {...props}>
      <div className={styles.modalContent}>
        {numberOfEdits !== 0 && currentEditIndex !== undefined && (
          <p className={styles.currentEdit}>
            {interpolate(
              product.content.canvas.edit_progress_label ||
                CANVAS_FALLBACK_CONTENT.edit_progress_label,
              {
                currentEdit: currentEditIndex + 1,
                numberOfEdits,
              }
            )}
          </p>
        )}

        <FormProvider {...methods}>
          {preview && (
            <ErrorBoundary>
              <SliceZone
                components={previewComponents}
                slices={preview.slices}
              />
            </ErrorBoundary>
          )}

          <form
            aria-label={interpolate(formDocument.data.title || "")}
            className={styles.form}
            onSubmit={(event) => {
              event.stopPropagation();
              methods.handleSubmit(onSubmit)(event);
            }}
          >
            <div className={styles.fields}>
              {(formDocument.data.title || formDocument.data.subtitle) && (
                <div className={styles.titleContainer}>
                  {formDocument.data.title && (
                    <h1 className={styles.title}>
                      {interpolate(formDocument.data.title)}
                    </h1>
                  )}

                  {formDocument.data.subtitle && (
                    <h2 className={styles.subtitle}>
                      {interpolate(formDocument.data.subtitle)}
                    </h2>
                  )}
                </div>
              )}

              {tabs ? (
                <TabbedForm
                  activeTabIndex={activeTabIndex}
                  onTabChange={onTabChange}
                  tabs={tabs}
                />
              ) : (
                <ErrorBoundary>
                  <SliceZone
                    components={fieldComponents}
                    slices={formDocument.data.slices}
                  />
                </ErrorBoundary>
              )}
            </div>

            <div className={styles.stickyActions}>
              <PrimaryButton isLoading={status === "loading"} type="submit">
                {status === "loading"
                  ? interpolate(
                      product.content.canvas.form_loading_label ||
                        CANVAS_FALLBACK_CONTENT.form_loading_label
                    )
                  : interpolate(
                      product.content.canvas.form_submit_button_label ||
                        CANVAS_FALLBACK_CONTENT.form_submit_button_label
                    )}
              </PrimaryButton>

              {onStepEdit && (
                <div className={styles.stepActions}>
                  <TertiaryButton
                    color="black"
                    isLoading={status === "loading"}
                    type="button"
                    onClick={() =>
                      methods.handleSubmit((data) =>
                        onSubmitAndStep(data, "previous")
                      )()
                    }
                  >
                    <TertiaryButton.Icon Component={ChevronLeftIcon} />

                    {interpolate(
                      product.content.canvas.previous_edit_button_label ||
                        CANVAS_FALLBACK_CONTENT.previous_edit_button_label
                    )}
                  </TertiaryButton>

                  <TertiaryButton
                    color="black"
                    isLoading={status === "loading"}
                    type="button"
                    onClick={() =>
                      methods.handleSubmit((data) =>
                        onSubmitAndStep(data, "next")
                      )()
                    }
                  >
                    {interpolate(
                      product.content.canvas.next_edit_button_label ||
                        CANVAS_FALLBACK_CONTENT.next_edit_button_label
                    )}

                    <TertiaryButton.Icon Component={ChevronRightIcon} />
                  </TertiaryButton>
                </div>
              )}
            </div>
          </form>
        </FormProvider>
      </div>
    </Modal>
  );
}
